// Composition layer for the map-first prototype.
//
// Summary load uses the dedicated /v1/farms/:id/blocks/summary endpoint.
// Detail load (panel content) still fans out across per-block endpoints
// because the panel is opened on click and the latency is hidden by the
// react-query loading state.

import type { Feature, FeatureCollection, Polygon } from "geojson";

import i18n from "@/i18n";
import { getBlock, listBlocks, type Block, type BlockDetail } from "@/api/blocks";
import { getBlocksSummary } from "@/api/blocksSummary";
import { localizedField, localizedName } from "@/lib/localizedField";
import { listBlockCrops, type BlockCropAssignment } from "@/api/cropAssignments";
import {
  listCrops,
  listCropVarieties,
  listVarietyStrains,
  type Crop,
  type CropVariety,
  type CropVarietyStrain,
} from "@/api/crops";
import { getFarm, type FarmDetail } from "@/api/farms";
import { getTimeseries, type IndexCode as ApiIndexCode } from "@/api/indices";
import { listAlerts } from "@/api/alerts";
import { listBlockHealth, type BlockIntegrationHealth } from "@/api/integrationsHealth";
import { listIrrigationSchedules, type IrrigationSchedule } from "@/api/irrigation";
import { listCalendar, listPlans, type Plan } from "@/api/plans";
import { listRecommendations, type Recommendation } from "@/api/recommendations";
import { listSignalObservations, type SignalObservation } from "@/api/signals";
import { getForecast, type ForecastResponse } from "@/api/weather";

import { mapAlertSeverity } from "./health";
import type {
  Health,
  HealthReason,
  HealthSource,
  IndexCode,
  IndexSeries,
  UnitAlert,
  UnitDetail,
  UnitFeatureProps,
  UnitIntegration,
  UnitSummary,
} from "./types";

function num(s: string | null | undefined): number | null {
  if (s == null || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function trend(series: { time: string; value: number | null }[]): number | null {
  // Last point - point at most 7d before. Skip nulls.
  const valid = series.filter((p) => p.value != null);
  if (valid.length < 2) return null;
  const last = valid[valid.length - 1];
  const lastTime = new Date(last.time).getTime();
  const cutoff = lastTime - 7 * 24 * 3600 * 1000;
  const prior = [...valid].reverse().find((p) => new Date(p.time).getTime() <= cutoff);
  const base = prior ?? valid[0];
  return (last.value as number) - (base.value as number);
}

function asoIndexSeries(points: { time: string; mean: string | null }[]): IndexSeries {
  const series_30d = points.map((p) => ({ time: p.time, value: num(p.mean) }));
  const valid = series_30d.filter((p) => p.value != null);
  const current = valid.length > 0 ? (valid[valid.length - 1].value as number) : null;
  return { current, trend_7d_delta: trend(series_30d), series_30d };
}

function isoNDays(n: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - n * 24 * 3600 * 1000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function blockShortName(b: Block): string {
  // Falls back to the code when the block has no name at all, which is the
  // common case: `name` is optional on a block, `code` never is.
  const en = b.name?.trim() || b.code;
  return localizedName(i18n.language, en, b.name_ar);
}

function specUnitType(b: Block): "block" | "pivot" | "pivot_section" {
  if (b.unit_type === "pivot_sector") return "pivot_section";
  return b.unit_type;
}

// ----- summary load --------------------------------------------------------

export interface MapSummary {
  farm: FarmDetail;
  blocks: Block[];
  geojson: FeatureCollection<Polygon, UnitFeatureProps>;
  summaries: Record<string, UnitSummary>;
  // Active season plan for the farm (status === "active"), if any.
  activePlan: Plan | null;
}

export async function loadMapSummary(farmId: string, at?: string | null): Promise<MapSummary> {
  // Parallel summary fan-out. Farm + blocks + summary + (best-effort)
  // active plan.
  //
  // Integration health is deliberately NOT in here. It used to be, and
  // because this is a Promise.all the page rendered at the speed of the
  // slowest member — which was the health endpoint at ~7 s on a 36-block
  // farm. Nothing on the map reads it: it feeds two rows in the block
  // panel, so it loads separately via `loadBlockHealth` and the panel
  // fills in when it arrives. See docs/ or the git history for the
  // measured breakdown.
  const [farm, blocksPage, summaryResp, plans] = await Promise.all([
    getFarm(farmId),
    listBlocks(farmId, { limit: 200, include_boundary: true }),
    // `at` reaches the alert rollup only. The block roster and the index
    // values are not as-of: a block that exists today existed on the pass
    // being drawn, and the console reads index values through the scene
    // routes, not through here.
    getBlocksSummary(farmId, at),
    safePlans(farmId),
  ]);
  const blocks = blocksPage.items;
  const activePlan = plans.find((p) => p.status === "active") ?? null;

  const summaryByBlock = new Map(summaryResp.units.map((u) => [u.id, u]));

  // Boundaries ride along on the list call (include_boundary). A farm with
  // N blocks used to cost N extra GET /blocks/{id} requests here; at a few
  // dozen blocks that burst exhausted the API's DB connection pool and the
  // whole page failed with "Couldn't load this farm". Only blocks the list
  // did not carry a boundary for are fetched, and then a few at a time.
  const boundaryById = new Map<string, Polygon>();
  for (const b of blocks) if (b.boundary) boundaryById.set(b.id, b.boundary);
  const missing = blocks.filter((b) => !boundaryById.has(b.id));
  if (missing.length > 0) {
    const fetched = await mapWithConcurrency(missing, 4, (b) => safeBlock(b.id));
    for (const d of fetched) if (d) boundaryById.set(d.id, d.boundary);
  }

  const summaries: Record<string, UnitSummary> = {};
  const features: Feature<Polygon, UnitFeatureProps>[] = [];

  // Identify pivots that have sectors → mark them as logical groups.
  const pivotChildren = new Map<string, string[]>();
  for (const b of blocks) {
    if (b.unit_type === "pivot_sector" && b.parent_unit_id) {
      const arr = pivotChildren.get(b.parent_unit_id) ?? [];
      arr.push(b.id);
      pivotChildren.set(b.parent_unit_id, arr);
    }
  }

  for (const b of blocks) {
    const boundary = boundaryById.get(b.id);
    if (!boundary) continue;
    const isLogicalPivot = b.unit_type === "pivot" && (pivotChildren.get(b.id)?.length ?? 0) > 0;

    const apiSummary = summaryByBlock.get(b.id);
    const summary: UnitSummary = {
      id: b.id,
      health: isLogicalPivot ? "unknown" : (apiSummary?.health ?? "unknown"),
      // A logical pivot has no health of its own — it is the union of its
      // sectors — so it has no reason either. Carrying one would put a
      // sentence under a chip that reads "Unknown" for a structural reason,
      // not an agronomic one.
      health_reason: isLogicalPivot ? null : (apiSummary?.health_reason ?? null),
      health_source: isLogicalPivot ? null : (apiSummary?.health_source ?? null),
      health_definition_version: isLogicalPivot
        ? null
        : (apiSummary?.health_definition_version ?? null),
      has_alert: !isLogicalPivot && (apiSummary?.alert_count ?? 0) > 0,
      alert_severity: isLogicalPivot ? null : (apiSummary?.alert_severity ?? null),
      alert_count: isLogicalPivot ? 0 : (apiSummary?.alert_count ?? 0),
      alert_action_type: isLogicalPivot ? null : (apiSummary?.alert_action_type ?? null),
      ndvi_current: apiSummary?.ndvi_current ?? null,
      ndre_current: apiSummary?.ndre_current ?? null,
      ndwi_current: apiSummary?.ndwi_current ?? null,
      grid_product_id: apiSummary?.grid_product_id ?? null,
    };
    if (!isLogicalPivot) summaries[b.id] = summary;

    const today = new Date().toISOString().slice(0, 10);
    const isFuture = Boolean(b.active_from && b.active_from > today);
    features.push({
      type: "Feature",
      id: b.id,
      geometry: boundary,
      properties: {
        id: b.id,
        type: isLogicalPivot ? "pivot_logical_group" : specUnitType(b),
        parent_pivot_id: b.parent_unit_id,
        is_logical_pivot: isLogicalPivot,
        name: blockShortName(b),
        health: summary.health,
        has_alert: summary.has_alert,
        alert_severity: summary.alert_severity ?? "none",
        alert_count: summary.alert_count,
        alert_action_type: summary.alert_action_type,
        is_future: isFuture,
        // Overwritten per-block by the page from the weather-risk summary
        // (PR-R4b); "none" is the no-risk default the fill expression treats
        // as transparent.
        risk_level: "none",
      },
    });
  }

  return {
    farm,
    blocks,
    geojson: { type: "FeatureCollection", features },
    summaries,
    activePlan,
  };
}

/**
 * Per-block integration health for a farm, indexed by block_id.
 *
 * Off the critical path on purpose — see `loadMapSummary`. Best-effort:
 * the capability behind it (`tenant.read_integration_health`) is only
 * granted to TenantOwner/TenantAdmin, so everyone else gets a 403 here
 * and simply sees no integration rows, exactly as before.
 */
export async function loadBlockHealth(
  farmId: string,
): Promise<Record<string, BlockIntegrationHealth>> {
  const rows = await safeBlockHealth(farmId);
  const byBlock: Record<string, BlockIntegrationHealth> = {};
  for (const h of rows) byBlock[h.block_id] = h;
  return byBlock;
}

/** Shape a health row into what the block panel renders. */
export function toUnitIntegration(h: BlockIntegrationHealth | null): UnitIntegration | null {
  if (!h) return null;
  return {
    weather: {
      active_subs: h.weather_active_subs,
      last_sync_at: h.weather_last_sync_at,
      last_failed_at: h.weather_last_failed_at,
      failed_24h: h.weather_failed_24h,
      running_count: h.weather_running_count,
      overdue_count: h.weather_overdue_count,
    },
    imagery: {
      active_subs: h.imagery_active_subs,
      last_sync_at: h.imagery_last_sync_at,
      last_failed_at: null,
      failed_24h: h.imagery_failed_24h,
      running_count: h.imagery_running_count,
      overdue_count: h.imagery_overdue_count,
    },
  };
}

// ----- detail load ---------------------------------------------------------

const detailCache = new Map<string, { at: number; value: UnitDetail }>();
const DETAIL_TTL_MS = 30_000;

// Alert text, recommendations and crop names are resolved to ONE language
// while the detail is built, so the cache entry belongs to that language.
// Keyed on the block alone, switching to Arabic replayed the English payload
// for up to DETAIL_TTL_MS — the react-query key already carries `i18n.language`,
// so it refetched and this cache handed the stale object straight back.
function detailCacheKey(blockId: string, lang: string | undefined): string {
  return `${blockId}|${lang ?? "en"}`;
}

export async function loadUnitDetail(args: {
  farmId: string;
  blockId: string;
  blocksById: Map<string, Block>;
  activePlan?: Plan | null;
  // The block's health as the server classified it, taken from the summary
  // the caller already holds (`MapSummary.summaries[blockId].health`). The
  // dock used to re-derive this in the browser from its own 30-day NDVI
  // window, which is a second answer to a question the map had already
  // answered. Callers that have no summary pass nothing and get "unknown".
  summaryHealth?: Health | null;
  // Travels with `summaryHealth` for the same reason: the dock renders the
  // server's answer, and a class without its reason is the half of it that
  // cannot be explained.
  summaryHealthReason?: HealthReason | null;
  summaryHealthSource?: HealthSource | null;
  summaryHealthVersion?: number | null;
}): Promise<UnitDetail> {
  const lang = i18n.language;
  const health: Health = args.summaryHealth ?? "unknown";
  const healthReason: HealthReason | null = args.summaryHealthReason ?? null;
  const healthSource: HealthSource | null = args.summaryHealthSource ?? null;
  const healthVersion: number | null = args.summaryHealthVersion ?? null;
  const cacheKey = detailCacheKey(args.blockId, lang);
  const cached = detailCache.get(cacheKey);
  // Health is not part of what this cache exists to hold. It rides the
  // summary's 60s poll, so a cache hit re-stamps the current value rather
  // than replaying the one captured up to DETAIL_TTL_MS ago.
  if (cached && Date.now() - cached.at < DETAIL_TTL_MS)
    return {
      ...cached.value,
      health,
      health_reason: healthReason,
      health_source: healthSource,
      health_definition_version: healthVersion,
    };

  const block = args.blocksById.get(args.blockId);
  if (!block) throw new Error(`Block ${args.blockId} not in farm`);

  const { from, to } = isoNDays(30);
  const sinceObs = new Date(Date.now() - 30 * 86_400_000).toISOString();

  const [
    blockDetail,
    indicesNdvi,
    indicesNdre,
    indicesNdwi,
    blockAlerts,
    irrigation,
    recs,
    weather,
    calendar,
    cropAssignments,
    signalObs,
  ] = await Promise.all([
    getBlock(args.blockId),
    safeTimeseries(args.blockId, "ndvi", from, to),
    safeTimeseries(args.blockId, "ndre", from, to),
    safeTimeseries(args.blockId, "ndwi", from, to),
    listAlerts({ block_id: args.blockId, status: "open", limit: 50 }),
    safeIrrigation(args.farmId),
    safeRecommendations(args.blockId),
    safeForecast(args.blockId),
    safeCalendar(args.farmId, from, to),
    safeBlockCrops(args.blockId),
    safeSignalObs(args.blockId, sinceObs),
  ]);

  const indexBundle: Record<IndexCode, IndexSeries> = {
    ndvi: asoIndexSeries(indicesNdvi),
    ndre: asoIndexSeries(indicesNdre),
    ndwi: asoIndexSeries(indicesNdwi),
  };

  const sortedSchedules = irrigation
    .filter((s) => s.block_id === args.blockId)
    .sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
  const today = new Date().toISOString().slice(0, 10);
  const last = [...sortedSchedules].reverse().find((s) => s.status === "applied");
  const next = sortedSchedules.find((s) => s.status === "pending" && s.scheduled_for >= today);

  const todayStr = new Date().toISOString().slice(0, 10);
  const next7dCutoff = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  const acts = (calendar?.activities ?? [])
    .filter((a) => a.block_id === args.blockId)
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
    .slice(0, 12)
    .map((a) => ({
      date: a.scheduled_date,
      label: a.activity_type.charAt(0).toUpperCase() + a.activity_type.slice(1).replace(/_/g, " "),
      // The raw enum, so a caller can run it through the `activityType.*`
      // catalogue instead of the English humanisation above.
      activity_type: a.activity_type,
      phase:
        a.scheduled_date >= todayStr && a.scheduled_date <= next7dCutoff
          ? ("next7d" as const)
          : ("later" as const),
    }));

  const weatherDays = (weather?.days ?? []).slice(0, 3).map((d, i) => ({
    day: i === 0 ? "Today" : new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }),
    date: d.date,
    temp_c_max: num(d.high_c),
  }));

  // The assignment whose validity range contains today. `is_current` is a
  // stored flag that only moves on assignment, so a finished season would
  // otherwise keep showing as the block's crop.
  const currentCrop = cropAssignments.find((c) => c.is_active_now) ?? null;
  const cropAssignmentSummary = await resolveCropAssignment(currentCrop, lang);
  const signals = condenseSignals(signalObs);

  const detail: UnitDetail = {
    id: block.id,
    name: blockShortName(block),
    type: specUnitType(block),
    parent_pivot_id: block.parent_unit_id,
    // Was hardcoded null from the prototype, which quietly rendered every
    // block as unassigned wherever this field was read. Derived from the
    // block's current assignment; `crop_assignment` still carries the full
    // crop/variety/strain triple for callers that want it.
    crop: cropAssignmentSummary?.crop_name ?? null,
    area_ha: block.area_m2 / 10_000,
    health,
    health_reason: healthReason,
    health_source: healthSource,
    health_definition_version: healthVersion,
    last_updated: blockDetail.updated_at,
    alerts: blockAlerts
      .map((a) => {
        const sev = mapAlertSeverity(a.severity);
        if (!sev) return null;
        return {
          id: a.id,
          severity: sev,
          code: a.rule_code,
          message:
            localizedField(lang, a.diagnosis_en, a.diagnosis_ar) ??
            localizedField(lang, a.prescription_en, a.prescription_ar) ??
            a.rule_code,
          raised_at: a.created_at,
        };
      })
      .filter((x): x is UnitAlert => x !== null),
    indices: indexBundle,
    irrigation: {
      last: last
        ? {
            date: last.scheduled_for,
            volume_mm: Number(last.applied_volume_mm ?? last.recommended_mm) || 0,
          }
        : null,
      next: next
        ? {
            date: next.scheduled_for,
            volume_mm: Number(next.recommended_mm) || 0,
            is_emergency: next.scheduled_for === today,
          }
        : null,
      soil_moisture_pct: num(next?.soil_moisture_pct ?? last?.soil_moisture_pct ?? null),
      soil_status: classifySoil(num(next?.soil_moisture_pct ?? last?.soil_moisture_pct ?? null)),
    },
    // Sits directly under the alerts in the dock's Overview column, so it has
    // to follow the same language as they do — `text_en` alone left the whole
    // column half-translated in Arabic.
    recommendations: recs
      .map((r) => localizedField(lang, r.text_en, r.text_ar) ?? r.text_en)
      .slice(0, 5),
    activities: acts,
    weather_3d: weatherDays,
    plan: args.activePlan
      ? {
          season_label: args.activePlan.season_label,
          season_year: args.activePlan.season_year,
          name: args.activePlan.name,
          status: args.activePlan.status,
        }
      : null,
    responsible_membership_id: blockDetail.agronomist_membership_id ?? null,
    crop_assignment: cropAssignmentSummary,
    signals,
  };

  detailCache.set(cacheKey, { at: Date.now(), value: detail });
  return detail;
}

function classifySoil(pct: number | null): "optimal" | "low" | "critical" | "unknown" {
  if (pct == null) return "unknown";
  if (pct < 15) return "critical";
  if (pct < 25) return "low";
  return "optimal";
}

async function safeTimeseries(blockId: string, code: ApiIndexCode, from: string, to: string) {
  try {
    const ts = await getTimeseries(blockId, code, { granularity: "daily", from, to });
    return ts.points;
  } catch {
    return [];
  }
}
async function safeIrrigation(farmId: string): Promise<IrrigationSchedule[]> {
  try {
    return await listIrrigationSchedules(farmId, {});
  } catch {
    return [];
  }
}
async function safeRecommendations(blockId: string): Promise<Recommendation[]> {
  try {
    return await listRecommendations({ block_id: blockId, state: "open", limit: 10 });
  } catch {
    return [];
  }
}
async function safeForecast(blockId: string): Promise<ForecastResponse | null> {
  try {
    return await getForecast(blockId, { horizon_days: 3 });
  } catch {
    return null;
  }
}
async function safeCalendar(farmId: string, from: string, to: string) {
  try {
    return await listCalendar(farmId, from, to);
  } catch {
    return null;
  }
}
async function safePlans(farmId: string): Promise<Plan[]> {
  try {
    return await listPlans(farmId, {});
  } catch {
    return [];
  }
}
async function safeBlock(blockId: string): Promise<BlockDetail | null> {
  try {
    return await getBlock(blockId);
  } catch {
    return null;
  }
}

/**
 * Run `fn` over `items` with at most `limit` requests in flight.
 *
 * Guards the fallback path above: an unbounded Promise.all over every block
 * is what took the API's connection pool down in the first place.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (let i = next++; i < items.length; i = next++) out[i] = await fn(items[i]);
  });
  await Promise.all(workers);
  return out;
}

async function safeBlockHealth(farmId: string): Promise<BlockIntegrationHealth[]> {
  try {
    return await listBlockHealth(farmId);
  } catch {
    return [];
  }
}
async function safeBlockCrops(blockId: string): Promise<BlockCropAssignment[]> {
  try {
    return await listBlockCrops(blockId);
  } catch {
    return [];
  }
}
async function safeSignalObs(blockId: string, since: string): Promise<SignalObservation[]> {
  try {
    return await listSignalObservations({ block_id: blockId, since, limit: 50 });
  } catch {
    return [];
  }
}

// Tiny in-memory crop name cache so repeated block selections don't refetch
// the same /v1/crops rows.
const cropCache = new Map<string, Crop>();
const varietyCache = new Map<string, CropVariety>();
const strainCache = new Map<string, CropVarietyStrain>();

/** Catalogue rows are cached by id with BOTH names on them, so the language
 * is picked at read time — the caches survive a language switch untouched. */
function catalogName(
  row: { name_en: string; name_ar: string | null } | undefined,
  lang: string | undefined,
): string | null {
  if (!row) return null;
  return localizedField(lang, row.name_en, row.name_ar);
}

async function resolveCropAssignment(
  c: BlockCropAssignment | null,
  lang: string | undefined,
): Promise<UnitDetail["crop_assignment"]> {
  if (!c) return null;
  let cropName = "—";
  let varietyName: string | null = null;
  let strainName: string | null = null;
  const cached = cropCache.get(c.crop_id);
  if (cached) cropName = catalogName(cached, lang) ?? "—";
  else {
    try {
      const crops = await listCrops();
      for (const crop of crops) cropCache.set(crop.id, crop);
      cropName = catalogName(cropCache.get(c.crop_id), lang) ?? "—";
    } catch {
      // fall through with placeholder
    }
  }
  if (c.crop_variety_id) {
    const v = varietyCache.get(c.crop_variety_id);
    if (v) varietyName = catalogName(v, lang);
    else {
      try {
        const vs = await listCropVarieties(c.crop_id);
        for (const variety of vs) varietyCache.set(variety.id, variety);
        varietyName = catalogName(varietyCache.get(c.crop_variety_id), lang);
      } catch {
        varietyName = null;
      }
    }
  }
  if (c.crop_variety_strain_id && c.crop_variety_id) {
    const s = strainCache.get(c.crop_variety_strain_id);
    if (s) strainName = catalogName(s, lang);
    else {
      try {
        const ss = await listVarietyStrains(c.crop_variety_id);
        for (const strain of ss) strainCache.set(strain.id, strain);
        strainName = catalogName(strainCache.get(c.crop_variety_strain_id), lang);
      } catch {
        strainName = null;
      }
    }
  }
  return {
    id: c.id,
    crop_name: cropName,
    variety_name: varietyName,
    strain_name: strainName,
    crop_path: c.crop_path,
    season_label: c.season_label,
    planting_date: c.planting_date,
    growth_stage: c.growth_stage,
    status: c.status,
  };
}

function condenseSignals(obs: SignalObservation[]): UnitDetail["signals"] {
  // Keep only the most recent observation per signal_code.
  const byCode = new Map<string, SignalObservation>();
  for (const o of obs) {
    const prev = byCode.get(o.signal_code);
    if (!prev || o.time > prev.time) byCode.set(o.signal_code, o);
  }
  return Array.from(byCode.values())
    .sort((a, b) => b.time.localeCompare(a.time))
    .slice(0, 8)
    .map((o) => ({
      code: o.signal_code,
      value: formatSignalValue(o),
      unit: null,
      recorded_at: o.time,
    }));
}

function formatSignalValue(o: SignalObservation): string {
  if (o.value_numeric != null) return o.value_numeric;
  if (o.value_categorical != null) return o.value_categorical;
  if (o.value_event != null) return o.value_event;
  if (o.value_boolean != null) return o.value_boolean ? "yes" : "no";
  if (o.value_geopoint != null) {
    return `${o.value_geopoint.latitude.toFixed(4)}, ${o.value_geopoint.longitude.toFixed(4)}`;
  }
  return "—";
}

export function clearDetailCache(): void {
  detailCache.clear();
}
